import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { API_V1, getImageUrl, fetchApi } from '../config/api';
import { refAmount } from '../utils/money';
import './ProductDetail.css';
import rollDinamitaImage from '../assets/Tipos de Rolls/ROLL Camaron - Dinamita.png';

const RECOMMENDED_LIMIT = 4;

function normalizeProductFromApi(p) {
  if (!p) return null;
  return {
    id: p.id ?? p.product_id,
    name: p.name ?? 'Producto',
    price: refAmount(p.price_eur),
    description: p.description ?? '',
    image: getImageUrl(p.image ?? p.thumbnail ?? p.thumbnail_url),
    presentation: p.presentation ?? '',
    allergen: p.allergen ?? '',
    isExclusive: p.is_exclusive ?? p.isExclusive ?? false,
    extras: p.extras ?? [],
  };
}

const ProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [recommendedLoading, setRecommendedLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSauce, setSelectedSauce] = useState(null);
  const [modalQuantity, setModalQuantity] = useState(1);

  useEffect(() => {
    if (!productId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchApi(`${API_V1}/products/${productId}`)
      .then((res) => {
        if (!res.ok) throw new Error('Producto no encontrado');
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        const raw = data.data ?? data;
        setProduct(normalizeProductFromApi(raw));
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Error al cargar el producto');
          setProduct(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [productId]);

  useEffect(() => {
    let cancelled = false;
    setRecommendedLoading(true);
    fetchApi(`${API_V1}/products?per_page=${RECOMMENDED_LIMIT + 4}&is_active=1`)
      .then((res) => res.ok ? res.json() : Promise.reject(new Error('Failed')))
      .then((data) => {
        if (cancelled) return;
        const list = data.data ?? data;
        const rawItems = Array.isArray(list) ? list : (list?.data ?? []);
        const normalized = rawItems
          .map(normalizeProductFromApi)
          .filter((p) => p && p.id != null && String(p.id) !== String(productId))
          .slice(0, RECOMMENDED_LIMIT);
        setRecommendedProducts(normalized);
      })
      .catch(() => {
        if (!cancelled) setRecommendedProducts([]);
      })
      .finally(() => {
        if (!cancelled) setRecommendedLoading(false);
      });
    return () => { cancelled = true; };
  }, [productId]);

  /** Extras associated with this product (from API). Empty if product has none. */
  const extrasList = product?.extras?.length
    ? product.extras.map((e) => ({
        id: e.id,
        name: e.title ?? e.name ?? 'Extra',
        price: refAmount(e.price_eur),
        image: getImageUrl(e.thumbnail ?? e.thumbnail_url ?? e.image),
      }))
    : [];

  const handleQuantityChange = useCallback((delta) => {
    setQuantity((q) => Math.max(1, q + delta));
  }, []);

  const handleProductClick = useCallback(
    (id) => navigate(`/producto/${id}`),
    [navigate]
  );

  const handleAddToCart = useCallback(() => {
    if (!product) return;
    addToCart({
      id: `product-${product.id}`,
      name: product.name,
      price: product.price,
      quantity,
      image: product.image || rollDinamitaImage,
      type: 'product',
    });
  }, [product, quantity, addToCart]);

  const handleSauceAddClick = (sauce) => {
    setSelectedSauce(sauce);
    setModalQuantity(1);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSauce(null);
    setModalQuantity(1);
  };

  const handleModalQuantityChange = useCallback((delta) => {
    setModalQuantity((q) => Math.max(1, q + delta));
  }, []);

  const handleModalAddToCart = () => {
    if (selectedSauce) {
      addToCart({
        id: selectedSauce.id,
        name: selectedSauce.name,
        price: selectedSauce.price,
        quantity: modalQuantity,
        image: selectedSauce.image,
        type: 'sauce'
      });
    }
    handleCloseModal();
  };

  if (loading && !product) {
    return (
      <section className="product-detail">
        <div className="container">
          <p className="combos-loading">Cargando producto…</p>
        </div>
      </section>
    );
  }

  if (error && !product) {
    return (
      <section className="product-detail">
        <div className="container">
          <p className="combos-error">{error}</p>
          <Link to="/categorias">Volver a categorías</Link>
        </div>
      </section>
    );
  }

  if (!product) return null;

  return (
    <section className="product-detail">
      <div className="container">
        <div className="breadcrumb">
          <Link to="/">Inicio</Link>
          <span> / </span>
          <Link to="/categorias">CATEGORÍAS</Link>
          <span> / </span>
          <span>{product.name}</span>
        </div>

        <div className="product-detail-content">
          <div className="product-detail-left">
            <div className="product-detail-image-container">
              <img
                src={product.image || rollDinamitaImage}
                alt={product.name}
                className="product-detail-image"
                onError={(e) => { e.target.src = rollDinamitaImage; }}
              />
            </div>
          </div>

          <div className="product-detail-right">
            <div className="product-detail-header">
              {product.isExclusive && (
                <div className="product-exclusive">
                  <span>Marumi Exclusive</span>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.35-4.35"></path>
                  </svg>
                </div>
              )}
              <h1 className="product-detail-name">{product.name}</h1>
            </div>

            {product.description && (
              <p className="product-detail-description">{product.description}</p>
            )}

            {product.presentation && (
              <div className="product-presentation">
                <h3 className="presentation-title">Presentación</h3>
                <button type="button" className="presentation-button">{product.presentation}</button>
              </div>
            )}

            {product.allergen && (
              <div className="product-allergen">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                <span>{product.allergen}</span>
              </div>
            )}

            <div className="product-price">
              <span className="price-label">Ref</span>
              <span className="price-value">{product.price}</span>
            </div>

            <div className="product-actions">
              <div className="quantity-selector">
                <button
                  type="button"
                  className="quantity-btn"
                  onClick={() => handleQuantityChange(-1)}
                  disabled={quantity <= 1}
                >
                  -
                </button>
                <span className="quantity-value">{quantity}</span>
                <button
                  type="button"
                  className="quantity-btn"
                  onClick={() => handleQuantityChange(1)}
                >
                  +
                </button>
              </div>
              <button type="button" className="add-to-cart-main-btn" onClick={handleAddToCart}>
                Añadir al Carrito
              </button>
            </div>

            {extrasList.length > 0 && (
            <div className="extra-sauces">
              <h3 className="extra-sauces-title">Extras</h3>
              <div className="sauces-list">
                {extrasList.map((extra) => (
                  <div key={extra.id} className="sauce-item">
                    <div className="sauce-icon">
                      <img
                        src={extra.image || rollDinamitaImage}
                        alt={extra.name}
                        className="sauce-image"
                        onError={(e) => { e.target.src = rollDinamitaImage; }}
                      />
                    </div>
                    <div className="sauce-info">
                      <span className="sauce-name">{extra.name}</span>
                      <span className="sauce-price">Ref {extra.price}</span>
                    </div>
                    <button
                      type="button"
                      className="sauce-add-btn"
                      onClick={() => handleSauceAddClick(extra)}
                    >
                      Añadir al Carrito
                    </button>
                  </div>
                ))}
              </div>
            </div>
            )}
          </div>
        </div>

        <div className="recommended-section">
          <h2 className="recommended-title">Te recomendamos</h2>
          {recommendedLoading ? (
            <p className="combos-loading">Cargando recomendados…</p>
          ) : recommendedProducts.length > 0 ? (
            <div className="recommended-grid">
              {recommendedProducts.map((item) => (
                <div
                  key={item.id}
                  className="recommended-card"
                  onClick={() => handleProductClick(item.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleProductClick(item.id);
                    }
                  }}
                >
                  <div className="recommended-image-container">
                    <img
                      src={item.image || rollDinamitaImage}
                      alt={item.name}
                      className="recommended-image"
                      onError={(e) => { e.target.src = rollDinamitaImage; }}
                    />
                  </div>
                  <div className="recommended-content">
                    <h3 className="recommended-name">{item.name}</h3>
                    <span className="recommended-price">Ref {item.price}</span>
                    <button
                      type="button"
                      className="recommended-add-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleProductClick(item.id);
                      }}
                    >
                      Añadir al Carrito
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="combos-loading">No hay productos recomendados.</p>
          )}
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && selectedSauce && (
        <>
          <div className="modal-overlay" onClick={handleCloseModal}></div>
          <div className="modal-container">
            <div className="modal-content">
              <div className="modal-header">
                <h2 className="modal-title">{selectedSauce.name}</h2>
                <button className="modal-close-btn" onClick={handleCloseModal}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
              <div className="modal-body">
                <div className="modal-image-container">
                  <img 
                    src={selectedSauce.image} 
                    alt={selectedSauce.name}
                    className="modal-image"
                  />
                </div>
                <div className="modal-info">
                  <div className="modal-price-top">
                    <span>Ref {selectedSauce.price}</span>
                  </div>
                  <div className="modal-quantity-section">
                    <label className="modal-quantity-label">Cantidad</label>
                    <button className="modal-quantity-option">3 Uni</button>
                  </div>
                  <div className="modal-price-bottom">
                    <span>Ref {selectedSauce.price}</span>
                  </div>
                  <div className="modal-quantity-selector">
                    <button 
                      className="modal-quantity-btn"
                      onClick={() => handleModalQuantityChange(-1)}
                      disabled={modalQuantity <= 1}
                    >
                      -
                    </button>
                    <span className="modal-quantity-value">{modalQuantity}</span>
                    <button 
                      className="modal-quantity-btn"
                      onClick={() => handleModalQuantityChange(1)}
                    >
                      +
                    </button>
                  </div>
                  <button 
                    className="modal-add-to-cart-btn"
                    onClick={handleModalAddToCart}
                  >
                    AÑADIR AL CARRITO
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  );
};

export default ProductDetail;

